import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as d3 from 'd3';
import { useApi } from '../../api/api';
import './ScallopParameterTracking.css';

const ScallopParameterTracking = ({ parameterName, stockName = null, previewMode = false, currentDbValue = null }) => {
  const CHART_HEIGHT = 500;
  const [parameterData, setParameterData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [managementAreas, setManagementAreas] = useState([]);

  const { getBasParameterById } = useApi();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Color scale for different management areas
  const colorScale = d3.scaleOrdinal(d3.schemeCategory10);

  // Set up resize observer for container width changes
  useEffect(() => {
    const measureContainer = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width > 0) {
          setContainerWidth(width);
        } else {
          const parent = containerRef.current.parentElement;
          if (parent) {
            const parentWidth = parent.getBoundingClientRect().width;
            if (parentWidth > 0) {
              const estimatedWidth = parentWidth * 0.75;
              setContainerWidth(estimatedWidth);
            }
          }
        }
      }
    };

    let resizeTimeout;
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        measureContainer();
      }, 100);
    });

    const currentContainer = containerRef.current;

    if (currentContainer) {
      resizeObserver.observe(currentContainer);
    }

    measureContainer();

    const initialTimer1 = setTimeout(measureContainer, 50);
    const initialTimer2 = setTimeout(measureContainer, 200);
    const initialTimer3 = setTimeout(measureContainer, 500);

    window.addEventListener('resize', measureContainer);

    return () => {
      clearTimeout(initialTimer1);
      clearTimeout(initialTimer2);
      clearTimeout(initialTimer3);
      clearTimeout(resizeTimeout);

      if (currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }

      window.removeEventListener('resize', measureContainer);
    };
  }, []);

  // Process data for chart - extract years from additional_factors
  const processDataForChart = useCallback((data) => {
    if (!data || !data.results) return;

    const allDataPoints = [];

    // Extract all data points from the papers
    data.results.forEach((paper) => {
      paper.extraction.results.forEach((result) => {
        // Get the value
        const value = parseFloat(result.value);
        if (isNaN(value)) return;

        // Filter for scallop-related data only
        const paperTitle = (paper.paper_title || '').toLowerCase();
        const species = result.species ? (Array.isArray(result.species) ? result.species : [result.species]) : [];
        const speciesStr = species.join(' ').toLowerCase();

        // Check if this is scallop-related data
        const isScallopData =
          paperTitle.includes('scallop') ||
          paperTitle.includes('placopecten') ||
          speciesStr.includes('scallop') ||
          speciesStr.includes('placopecten');

        if (!isScallopData) {
          console.log('Filtering out non-scallop paper:', paper.paper_title, 'Species:', species);
          return; // Skip non-scallop data
        }

        // DEBUG: Log the entire result object to see the structure
        console.log('Full result object:', JSON.stringify(result, null, 2));

        // Get management area with fallback options
        let managementArea =
          result.managementArea ||
          result.additional_factors?.management_area ||
          result.additional_factors?.region ||
          result.additional_factors?.area ||
          result.location?.region ||
          'Unknown';

        // Consolidate Georges Bank areas into one
        if (managementArea.toLowerCase().includes('georges bank')) {
          managementArea = 'Georges Bank';
        }

        // Log what we found
        console.log('Management area found:', managementArea, 'for result:', {
          managementArea: result.managementArea,
          additional_factors: result.additional_factors,
          location: result.location
        });

        // Get years from additional_factors
        const years = result.additional_factors?.years;
        if (!years || !Array.isArray(years)) {
          console.warn('No years found in additional_factors for result:', result);
          return;
        }

        // Create a data point for each year
        years.forEach(year => {
          allDataPoints.push({
            year: parseInt(year),
            value: value,
            paperTitle: paper.paper_title,
            raw_value: result.raw_value || '',
            units: data.standard_unit || '',
            managementArea: managementArea
          });
        });
      });
    });

    // Group by management area using D3
    const managementAreaGroups = d3.group(allDataPoints, d => d.managementArea);

    // Extract unique management areas
    const areas = Array.from(managementAreaGroups.keys()).sort();
    setManagementAreas(areas);

    // Format data for the chart
    const formattedData = areas.map(area => {
      const areaData = managementAreaGroups.get(area) || [];
      // Sort by year
      areaData.sort((a, b) => a.year - b.year);
      return {
        area: area,
        data: areaData
      };
    });

    setChartData(formattedData);
  }, []);

  // Fetch parameter data when selected parameter changes
  useEffect(() => {
    if (!parameterName) return;

    const fetchParameterData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        console.log(`Fetching scallop parameter data for: ${parameterName} (parameter_id: 95)`);
        // Use parameter_id = 95 for scallop natural mortality
        const response = await getBasParameterById(95);
        const data = response.data;
        console.log("Scallop parameter tracking data:", data);

        setParameterData(data);
        processDataForChart(data);

      } catch (err) {
        console.error('Error fetching parameter data:', err);
        setError('Failed to load parameter data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchParameterData();
  }, [parameterName, processDataForChart, getBasParameterById]);

  // Helper function to retrieve the standard unit
  const getStandardUnit = useCallback(() => {
    if (!parameterData) return '';
    return parameterData.standard_unit ? `(${parameterData.standard_unit})` : '';
  }, [parameterData]);

  // D3 visualization effect
  useEffect(() => {
    if (!svgRef.current || chartData.length === 0) return;

    const renderChart = () => {
      // Clear any existing visualization
      d3.select(svgRef.current).selectAll('*').remove();

      // Get the actual container width
      const containerElement = svgRef.current.parentElement;
      const containerRect = containerElement.getBoundingClientRect();

      let chartWidth = containerRect.width;

      if (chartWidth <= 0 || chartWidth < 100) {
        const parentElement = containerElement.parentElement;
        if (parentElement) {
          const parentRect = parentElement.getBoundingClientRect();
          if (parentRect.width > 0) {
            chartWidth = parentRect.width * 0.9;
          }
        }

        if (chartWidth <= 0) {
          chartWidth = Math.min(window.innerWidth * 0.6, 1000);
        }

        if (chartWidth <= 0) {
          chartWidth = 800;
        }
      }

      console.log('Chart container width:', chartWidth);

      const containerHeight = CHART_HEIGHT;

      // Set up margins with more space for legend
      const margin = { top: 30, right: 180, left: 70, bottom: 60 };

      // Calculate chart dimensions
      const width = chartWidth - margin.left - margin.right;
      const height = containerHeight - margin.top - margin.bottom;

      // Create SVG with explicit dimensions
      const svg = d3.select(svgRef.current)
        .attr('width', chartWidth)
        .attr('height', containerHeight);

      // Main chart group with transform
      const g = svg.append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

      // Flatten chartData back to individual points with managementArea
      const allPoints = [];
      chartData.forEach(areaData => {
        areaData.data.forEach(point => {
          allPoints.push(point);
        });
      });

      if (allPoints.length === 0) {
        g.append('text')
          .attr('x', width / 2)
          .attr('y', height / 2)
          .attr('text-anchor', 'middle')
          .style('fill', '#6b7280')
          .text('No data available');
        return;
      }

      // Get all years across all areas
      const allYears = [];
      chartData.forEach(areaData => {
        areaData.data.forEach(d => allYears.push(d.year));
      });

      // Get all values for y-scale
      const allValues = [];
      chartData.forEach(areaData => {
        areaData.data.forEach(d => allValues.push(d.value));
      });

      if (allYears.length === 0 || allValues.length === 0) {
        g.append('text')
          .attr('x', width / 2)
          .attr('y', height / 2)
          .attr('text-anchor', 'middle')
          .style('fill', '#6b7280')
          .text('No data available');
        return;
      }

      // Create scales
      const xScale = d3.scaleLinear()
        .domain([d3.min(allYears), d3.max(allYears)])
        .range([0, width - 10]);

      // Use fixed upper bound of 0.7 for y-axis
      const yScale = d3.scaleLinear()
        .domain([0, 0.7])
        .range([height, 0])
        .nice(); // This makes the domain end on nice round values

      // Create and add x-axis with smaller text
      const xAxis = d3.axisBottom(xScale)
        .tickFormat(d3.format('d')) // Format as integer years
        .ticks(8); // Limit number of ticks for less crowding

      g.append('g')
        .attr('transform', `translate(0, ${height})`)
        .call(xAxis)
        .selectAll('text')
        .style('fill', '#374151')
        .style('font-size', '11px'); // Smaller font

      // Add x-axis label
      g.append('text')
        .attr('transform', `translate(${width / 2}, ${height + 40})`)
        .style('text-anchor', 'middle')
        .style('fill', '#1f2937')
        .style('font-size', '12px')
        .style('font-weight', '500')
        .text('Year');

      // Create and add y-axis with more ticks for smoother intervals
      g.append('g')
        .call(d3.axisLeft(yScale).ticks(10))
        .selectAll('text')
        .style('fill', '#374151')
        .style('font-size', '11px'); // Smaller font

      // Add y-axis label
      g.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', -55)
        .attr('x', -height / 2)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .style('fill', '#1f2937')
        .style('font-size', '12px')
        .style('font-weight', '500')
        .text(`Natural Mortality (M) ${getStandardUnit()}`);

      // Add subtle grid lines
      g.append('g')
        .attr('class', 'grid-lines')
        .selectAll('line')
        .data(yScale.ticks(6))
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('x2', width - 10)
        .attr('y1', d => yScale(d))
        .attr('y2', d => yScale(d))
        .style('stroke', 'rgba(255, 255, 255, 0.05)')
        .style('stroke-width', 1)
        .style('stroke-dasharray', '2,3');

      // Create tooltip
      let tooltip = d3.select('body').select('.scallop-d3-tooltip');
      if (tooltip.empty()) {
        tooltip = d3.select('body').append('div')
          .attr('class', 'scallop-d3-tooltip custom-tooltip')
          .style('opacity', 0)
          .style('position', 'absolute')
          .style('pointer-events', 'none');
      }

      // Group data by management area using d3.group (allPoints already defined above)
      const managementAreaGroups = d3.group(allPoints, d => d.managementArea);

      // Create dynamic color scale based on actual management areas in the data
      const areaColorScale = d3.scaleOrdinal()
        .domain(Array.from(managementAreaGroups.keys()))
        .range(['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#ef4444', '#f97316', '#14b8a6', '#a855f7']);

      // Draw a line for each management area
      managementAreaGroups.forEach((points, managementArea) => {
        // Group points by year
        const yearGroups = d3.group(points, d => d.year);

        // Calculate average value for each year (aggregation)
        const aggregatedPoints = Array.from(yearGroups, ([year, yearPoints]) => {
          const avgValue = d3.mean(yearPoints, d => d.value);
          return {
            year: year,
            timestamp: new Date(year, 0, 1).getTime(),
            value: avgValue,
            managementArea: managementArea,
            minValue: d3.min(yearPoints, d => d.value),
            maxValue: d3.max(yearPoints, d => d.value),
            count: yearPoints.length,
            paperTitle: yearPoints[0].paperTitle, // Keep first paper for tooltip
            units: yearPoints[0].units
          };
        }).sort((a, b) => a.year - b.year);

        // Filter out extreme outliers using IQR method for smoother lines
        const values = aggregatedPoints.map(p => p.value);
        const sortedValues = [...values].sort((a, b) => a - b);
        const q1 = sortedValues[Math.floor(sortedValues.length * 0.25)];
        const q3 = sortedValues[Math.floor(sortedValues.length * 0.75)];
        const iqr = q3 - q1;
        const lowerBound = q1 - 1.5 * iqr;
        const upperBound = q3 + 1.5 * iqr;

        // Filter points to remove extreme outliers
        const filteredPoints = aggregatedPoints.filter(p =>
          p.value >= lowerBound && p.value <= upperBound
        );

        // Only draw if we have at least 2 points
        if (filteredPoints.length < 2) return;

        // Line generator with smoother curve
        const lineGenerator = d3.line()
          .x(d => xScale(d.year))
          .y(d => yScale(d.value))
          .curve(d3.curveCatmullRom.alpha(0.5)); // Smoother curve with tension

        // Draw line with better styling
        g.append('path')
          .datum(filteredPoints)
          .attr('class', `management-area-line area-${managementArea.replace(/\s+/g, '-')}`)
          .attr('fill', 'none')
          .attr('stroke', areaColorScale(managementArea))
          .attr('stroke-width', 1.5)
          .attr('opacity', 0.8)
          .attr('d', lineGenerator);

      });

      // Add horizontal line for current database value if provided
      if (currentDbValue != null && !isNaN(currentDbValue)) {
        const dbValueY = yScale(currentDbValue);

        // Add the horizontal line
        g.append('line')
          .attr('class', 'db-value-line')
          .attr('x1', 0)
          .attr('x2', width - 10)
          .attr('y1', dbValueY)
          .attr('y2', dbValueY)
          .attr('stroke', '#ff6b35')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '5,5')
          .attr('opacity', 0.8);

        // Add label for the database value line
        g.append('text')
          .attr('class', 'db-value-label')
          .attr('x', width - 15)
          .attr('y', dbValueY - 5)
          .attr('text-anchor', 'end')
          .attr('fill', '#ff6b35')
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .text(`Current: ${currentDbValue.toFixed(3)}`);
      }

      // Add invisible larger hit areas for tooltips (no visible data points)
      g.selectAll('.data-point-hitarea')
        .data(allPoints)
        .enter()
        .append('circle')
        .attr('class', 'data-point-hitarea')
        .attr('cx', d => xScale(d.year))
        .attr('cy', d => yScale(d.value))
        .attr('r', 8) // Larger invisible hit area for easier hovering
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          // Show a temporary highlight circle
          g.append('circle')
            .attr('class', 'temp-highlight')
            .attr('cx', xScale(d.year))
            .attr('cy', yScale(d.value))
            .attr('r', 5)
            .attr('fill', areaColorScale(d.managementArea))
            .attr('stroke', '#ffffff')
            .attr('stroke-width', 2)
            .style('pointer-events', 'none');

          const tooltipContent = `
            <p class="tooltip-title"><b>Management Area:</b> ${d.managementArea}</p>
            <p class="tooltip-date"><b>Year:</b> ${d.year}</p>
            <p class="tooltip-value"><b>M Value:</b> ${d.value.toFixed(3)} ${d.units}</p>
            <p class="tooltip-paper"><b>Source:</b> ${d.paperTitle}</p>
          `;

          tooltip
            .html(tooltipContent)
            .style('left', `${event.clientX + 10}px`)
            .style('top', `${event.clientY - 10}px`)
            .style('opacity', 1);
        })
        .on('mouseout', function() {
          // Remove temporary highlight
          g.selectAll('.temp-highlight').remove();
          tooltip.style('opacity', 0);
        });

      // Add legend with improved styling
      const legend = g.append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(${width + 25}, 10)`);

      // Add legend title
      legend.append('text')
        .attr('x', 0)
        .attr('y', -5)
        .style('fill', '#1f2937')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .text('Management Area');

      managementAreas.forEach((area, idx) => {
        const legendRow = legend.append('g')
          .attr('transform', `translate(0, ${idx * 24 + 10})`);

        // Draw line
        legendRow.append('line')
          .attr('x1', 0)
          .attr('x2', 18)
          .attr('y1', 8)
          .attr('y2', 8)
          .attr('stroke', areaColorScale(area))
          .attr('stroke-width', 2.5)
          .attr('opacity', 0.85);

        // Draw point
        legendRow.append('circle')
          .attr('cx', 9)
          .attr('cy', 8)
          .attr('r', 4)
          .attr('fill', areaColorScale(area))
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5)
          .attr('opacity', 0.9);

        // Add text
        legendRow.append('text')
          .attr('x', 24)
          .attr('y', 12)
          .style('fill', '#374151')
          .style('font-size', '11px')
          .text(area);
      });
    };

    renderChart();

    const retryTimeout1 = setTimeout(renderChart, 50);
    const retryTimeout2 = setTimeout(renderChart, 200);
    const retryTimeout3 = setTimeout(renderChart, 500);

    return () => {
      clearTimeout(retryTimeout1);
      clearTimeout(retryTimeout2);
      clearTimeout(retryTimeout3);
    };
  }, [chartData, containerWidth, parameterName, getStandardUnit, managementAreas, colorScale, currentDbValue]);

  return (
    <div className="scallop-parameter-tracking-container">
      {!previewMode && (<p>Track how natural mortality estimates for scallops have evolved across different management areas.</p>)}

      {isLoading ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading parameter data...</p>
        </div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          <div className="parameter-dashboard">
            <div className="chart-panel">
              <h3>
                {parameterName} by Management Area Over Time
                {parameterData && parameterData.standard_unit ? ` ${getStandardUnit()}` : ''}
              </h3>
              {chartData.length > 0 ? (
                <div
                  className="chart-container"
                  ref={containerRef}
                >
                  <svg ref={svgRef}></svg>
                </div>
              ) : (
                <p className="no-data-message">
                  No data available for the selected parameter.
                </p>
              )}
            </div>
          </div>

          {!previewMode && chartData.length > 0 && (
            <div className="data-table-section">
              <h3>Data Points</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Management Area</th>
                    <th>Year</th>
                    <th>Value</th>
                    <th>Paper Title</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((areaData) =>
                    areaData.data.map((point, index) => (
                      <tr key={`${areaData.area}-${index}`}>
                        <td>{areaData.area}</td>
                        <td>{point.year}</td>
                        <td>{point.raw_value || point.value.toFixed(3)}</td>
                        <td>{point.paperTitle}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScallopParameterTracking;
